import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Tag model matching the tags table.
 */
export interface Tag {
  id: string;
  name: string;
  code: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CreateTagPayload {
  name: string;
  code: string;
  description?: string | null;
  color?: string | null;
}

export interface UpdateTagPayload {
  name?: string;
  code?: string;
  description?: string | null;
  color?: string | null;
  is_active?: boolean;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const tagsKeys = {
  all: ['tags'] as const,
  list: () => [...tagsKeys.all, 'list'] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapTag(raw: Record<string, unknown>): Tag {
  return {
    id: raw.id as string,
    name: (raw.name || '') as string,
    code: (raw.code || '') as string,
    description: (raw.description ?? null) as string | null,
    color: (raw.color ?? null) as string | null,
    isActive: (raw.is_active ?? raw.isActive ?? true) as boolean,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all tags for the current tenant.
 */
export function useTagsList() {
  return useQuery({
    queryKey: tagsKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/tags');
      const raw = response.data.data || [];
      return raw.map(mapTag);
    },
  });
}

/**
 * Create a new tag.
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTagPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>('/tags', payload);
      return mapTag(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.list() });
      // Also invalidate the contacts tags query for consistency
      queryClient.invalidateQueries({ queryKey: ['contacts', 'tags'] });
    },
  });
}

/**
 * Update an existing tag.
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTagPayload }) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/tags/${id}`,
        data,
      );
      return mapTag(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'tags'] });
    },
  });
}

/**
 * Delete a tag (removes all contact associations as well).
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/tags/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'tags'] });
    },
  });
}
